import { useEffect, useMemo, useRef, useState } from 'react'
import { analyticsUi } from '../analyticsUiState'

/**
 * Лёгкая сортировка + колоночные фильтры-выпадашки для обычных <table> вкладок
 * «Аналитики» (без перевода на AG Grid — сохраняем кастомный рендер ячеек:
 * ссылки, цвета, бейджи). Сортировка/фильтр работают на клиенте по строкам,
 * которые уже пришли с бэка (лимиты top-N там подняты, чтобы фильтр видел все).
 *
 * Колонка описывается ColumnDef: `sort` — значение для сортировки (число/строка),
 * `filter` — строковое значение для set-фильтра (если колонка фильтруемая).
 */

export type SortDir = 'asc' | 'desc'

export interface ColumnDef<T> {
  key: string
  sort?: (row: T) => number | string
  filter?: (row: T) => string
}

/** Часть контроллера, нужная заголовку/фильтру — без дженерика по строке. */
export interface TableControls {
  sortKey: string | null
  sortDir: SortDir
  toggleSort: (key: string) => void
  isSortable: (key: string) => boolean
  isFilterable: (key: string) => boolean
  valuesFor: (key: string) => string[]
  selectedFor: (key: string) => Set<string> | null
  setFilter: (key: string, sel: Set<string> | null) => void
}

export interface AnalyticsTable<T> extends TableControls {
  /** Отфильтрованные + отсортированные строки для рендера. */
  view: T[]
}

export function useAnalyticsTable<T>(
  rows: T[],
  columns: ColumnDef<T>[],
  initialSort?: { key: string; dir: SortDir },
  /** Если задан — сортировка/фильтры сохраняются и восстанавливаются между
   *  переходами (напр. заход в заявку и возврат назад), ключ уникален на таблицу. */
  persistKey?: string,
): AnalyticsTable<T> {
  const restored = persistKey ? analyticsUi.getTable(persistKey) : undefined
  const [sortKey, setSortKey] = useState<string | null>(
    restored ? restored.sortKey : (initialSort?.key ?? null),
  )
  const [sortDir, setSortDir] = useState<SortDir>(
    restored ? restored.sortDir : (initialSort?.dir ?? 'desc'),
  )
  // key → множество выбранных значений; отсутствие ключа = «выбраны все».
  const [filters, setFilters] = useState<Record<string, Set<string>>>(() => {
    if (!restored) return {}
    const out: Record<string, Set<string>> = {}
    for (const [k, v] of Object.entries(restored.filters)) out[k] = new Set(v)
    return out
  })

  const colMap = useMemo(() => {
    const m = new Map<string, ColumnDef<T>>()
    for (const c of columns) m.set(c.key, c)
    return m
  }, [columns])

  const toggleSort = (key: string): void => {
    const col = colMap.get(key)
    if (!col?.sort) return
    // Первое направление: строки — по возрастанию (А→Я), числа — по убыванию
    // (большее сверху), как принято в гридах проекта. Реверсим ОТНОСИТЕЛЬНО
    // него, иначе для строковых колонок обратный порядок (Я→А) недостижим.
    const sample = rows.length ? col.sort(rows[0]!) : 0
    const firstDir: SortDir = typeof sample === 'string' ? 'asc' : 'desc'
    if (sortKey !== key) {
      setSortKey(key)
      setSortDir(firstDir)
    } else if (sortDir === firstDir) {
      setSortDir(firstDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(null) // третий клик — сброс сортировки
    }
  }

  // Distinct-значения фильтруемых колонок по ВСЕМ строкам (чтобы в выпадашке
  // были все опции, а не только оставшиеся после текущей фильтрации).
  const filterValues = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const c of columns) {
      if (!c.filter) continue
      const set = new Set<string>()
      for (const r of rows) set.add(c.filter(r))
      m.set(
        c.key,
        [...set].sort((a, b) => a.localeCompare(b, 'ru')),
      )
    }
    return m
  }, [rows, columns])

  // Домен значений фильтруемых колонок сменился (обычно — смена периода/данных):
  // сбрасываем набранные фильтры, иначе выбранный Set молча скрыл бы новые
  // значения набора, а исчезнувшие — продолжали бы фильтровать. Идентичные
  // данные (фоновый refetch) домен не меняют → фильтры сохраняются.
  const filterSig = useMemo(
    () => [...filterValues.entries()].map(([k, vs]) => `${k}${vs.join('')}`).join(''),
    [filterValues],
  )
  // null → домен ещё ни разу не был непустым. Первый непустой домен (первая
  // загрузка данных) фиксируем БЕЗ сброса — иначе стёрли бы восстановленные из
  // стора фильтры. Сброс только при ПОСЛЕДУЮЩЕЙ смене домена (смена периода).
  const prevSig = useRef<string | null>(null)
  useEffect(() => {
    if (filterSig === '') return
    if (prevSig.current === null) {
      prevSig.current = filterSig
      return
    }
    if (prevSig.current !== filterSig) {
      prevSig.current = filterSig
      setFilters((f) => (Object.keys(f).length ? {} : f))
    }
  }, [filterSig])

  // Сохраняем сортировку/фильтры в стор — переживают уход со страницы и возврат.
  useEffect(() => {
    if (!persistKey) return
    const serFilters: Record<string, string[]> = {}
    for (const [k, s] of Object.entries(filters)) serFilters[k] = [...s]
    analyticsUi.setTable(persistKey, { sortKey, sortDir, filters: serFilters })
  }, [persistKey, sortKey, sortDir, filters])

  const view = useMemo(() => {
    let out = rows
    const active = Object.entries(filters).filter(([, s]) => s)
    if (active.length) {
      out = out.filter((r) =>
        active.every(([key, set]) => {
          const col = colMap.get(key)
          if (!col?.filter || !set) return true
          return set.has(col.filter(r))
        }),
      )
    }
    if (sortKey) {
      const col = colMap.get(sortKey)
      if (col?.sort) {
        const acc = col.sort
        out = [...out].sort((a, b) => {
          const va = acc(a)
          const vb = acc(b)
          const cmp =
            typeof va === 'number' && typeof vb === 'number'
              ? va - vb
              : String(va).localeCompare(String(vb), 'ru')
          return sortDir === 'asc' ? cmp : -cmp
        })
      }
    }
    return out
  }, [rows, filters, sortKey, sortDir, colMap])

  return {
    view,
    sortKey,
    sortDir,
    toggleSort,
    isSortable: (key) => colMap.get(key)?.sort != null,
    isFilterable: (key) => colMap.get(key)?.filter != null,
    valuesFor: (key) => filterValues.get(key) ?? [],
    selectedFor: (key) => filters[key] ?? null,
    setFilter: (key, sel) =>
      setFilters((f) => {
        const next = { ...f }
        if (sel === null) delete next[key]
        else next[key] = sel
        return next
      }),
  }
}
